import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../redux/HooksRedux';
import { RootState } from '../../../redux/rootReducer';
import {
  AdminLevelFormI,
  LogDisplayItem,
  LogEntry,
  Meta,
  paramsI,
  ValidationObj
} from '../../../../types/interface';
import { AdminLevelValidationSet } from 'utils/validations/ValidationsSet/AdminLevelValidationSet';
import validationFunctions from 'utils/validationUtils';
import {
  capitalizeFirstLetter,
  checkAccess,
  logEntriesToDisplayItems,
  parseIntoNumber
} from 'utils/helpers';
import {
  GERMAN,
  logPerPage,
  LogType,
  modules,
  SELECTED_LANGUAGE
} from 'utils/enums/misc';
import { getCookie } from 'utils/cookieUtils';
import * as AdminLevelAction from '../../../redux/AdminLevel/AdminLevelAction';
import * as logAction from '../../../redux/logs/logAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { ADMIN_LEVEL } from 'constant/RoutesUrls';
import { AxiosError, AxiosResponse } from 'axios';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { Card, Dropdown, Form } from 'react-bootstrap';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { t } from 'i18next';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardTab from 'base/StandardTab/StandardTab';
import { tabData } from 'utils/staticData';
import InfinityScroll from 'base/InfonityScroll/InfinityScroll';
import LogTimeline from 'components/timelines/LogTimeline';
import StandardInput from 'base/StandardInput/StandardInput';

const AdminLevelForm = () => {
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const editAdminLevelId = URLSearchParams.get('editAdminLevelId');
  const dispatch = useAppDispatch();
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  console.log(editAdminLevelId, 'editAdminLevelId');

  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAdminLevel, setDeleteAdminLevel] = useState<{
    admin_level_id: number;
    showPopup: boolean;
  }>({
    admin_level_id: 0,
    showPopup: false
  });

  // Logs states
  const [logCurrentPage, setLogCurrentPage] = useState(1);
  const [logs, setLogs] = useState<LogDisplayItem[]>([]);
  const [activeTab, setActiveTab] = useState<LogType | null>(LogType.FORM);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [logLoading, setLogLoading] = useState(false);

  const [formData, setFormData] = useState<AdminLevelFormI>({
    level_name: '',
    level_order: 0,
    level_name_german: '',
    description_en: '',
    description_de: ''
  });

  const handleSubmit = async (e: React.FormEvent, isArchive?: boolean) => {
    e.preventDefault();
    setIsLoading(true);

    let validations = { ...AdminLevelValidationSet };
    if (editAdminLevelId) {
      validations = { ...validations, level_order: [] };
    }

    const validate: boolean = validationFunctions.checkFormValidity(
      {
        ...formData
      },
      validations
    );

    if (!validate) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    if (
      editAdminLevelId &&
      checkAccess([modules.ADMIN_LEVEL.edit], userPermission)
    ) {
      try {
        const response = await dispatch(
          AdminLevelAction.updateAdminLevel(Number(editAdminLevelId), {
            ...formData
          })
        );

        if (response.status === 200) {
          successNotification(t('admin_level.notification.update_success'));
          navigate(ADMIN_LEVEL);
          setIsLoading(false);
        } else {
          errorNotification(response.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorNotification(error.message);
        } else if (
          error instanceof AxiosError &&
          error?.response?.data?.message
        ) {
          errorNotification(
            error?.response?.data?.message || error.response?.data?.error
          );
        }
        console.log(error);
        setIsLoading(false);
      }
    } else if (checkAccess([modules.ADMIN_LEVEL.create], userPermission)) {
      try {
        const response = await dispatch(
          AdminLevelAction.createAdminLevel(formData)
        );

        if (response?.status === 201) {
          successNotification(t('admin_level.notification.create_success'));
          navigate(ADMIN_LEVEL);
          setIsLoading(false);
        } else {
          errorNotification(response?.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    } else {
      navigate(ADMIN_LEVEL);
      setIsLoading(false);
    }
  };

  const getSingleAdminLevel = async () => {
    if (!checkAccess([modules.ADMIN_LEVEL.edit], userPermission)) {
      navigate(ADMIN_LEVEL);
      return;
    }
    setIsLoading(true);
    try {
      const response = await dispatch(
        AdminLevelAction.getSingleAdminLevel(Number(editAdminLevelId))
      );

      if (response?.status === 200) {
        setFormData({
          level_name: response.data.data.level_name,
          level_name_german: response.data.data.level_name_de,
          level_order: response.data.data.level_order,
          description_en: response.data.data.description_en || '',
          description_de: response.data.data.description_de || ''
        });
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        navigate(ADMIN_LEVEL);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (editAdminLevelId) {
      getSingleAdminLevel();
    }
  }, []);

  const handleDeleteAdminLevel = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        AdminLevelAction.deleteAdminLevel(deleteAdminLevel.admin_level_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteAdminLevel({ admin_level_id: 0, showPopup: false });
        navigate(ADMIN_LEVEL);
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setDeleteAdminLevel({ admin_level_id: 0, showPopup: false });
        setIsLoading(false);
      }
    } catch (error) {
      setDeleteAdminLevel({ admin_level_id: 0, showPopup: false });
      console.log(error);
      setIsLoading(false);
    }
  };

  const renderDeletePopup = () => {
    if (deleteAdminLevel.showPopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteAdminLevel({ admin_level_id: 0, showPopup: false })
          }
          onConfirm={handleDeleteAdminLevel}
        />
      );
    }
  };

  const getAdminLevelsLog = useCallback(
    async (adminLevelId: number) => {
      try {
        const params: paramsI = {
          page: 1,
          limit: logPerPage * logCurrentPage,
          keyword: ''
        };
        const response: AxiosResponse<{ logs: LogEntry[]; meta: Meta }> =
          await dispatch(logAction.getLogByAdminLevelId(adminLevelId, params));
        if (params.limit >= response.data.meta.totalRecords) {
          setHasMoreLogs(false);
        } else {
          setHasMoreLogs(true);
        }

        setLogs(
          logEntriesToDisplayItems(
            response.data.logs,
            getCookie(SELECTED_LANGUAGE) === GERMAN
          )
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLogLoading(false);
      }
    },
    [logCurrentPage, logPerPage]
  );

  useEffect(() => {
    if (editAdminLevelId && activeTab === LogType.LOGS) {
      getAdminLevelsLog(Number(editAdminLevelId));
    }
  }, [editAdminLevelId, logCurrentPage]);

  useEffect(() => {
    if (activeTab === LogType.LOGS && logs.length === 0) {
      getAdminLevelsLog(Number(editAdminLevelId));
    }
  }, [activeTab]);

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="py-2">
            <div className="row justify-content-between align-items-center gap-2">
              <div className="w-auto">
                <h3>{capitalizeFirstLetter(t('admin_level.form.header'))} </h3>
              </div>
              <div className="d-flex gap-2 w-auto">
                <StandardButton
                  onClick={() => {
                    navigate(ADMIN_LEVEL);
                  }}
                  buttonText={t('general.common_text.text_15')}
                  variant="outline-danger"
                />
                <StandardButton
                  onClick={handleSubmit}
                  type="submit"
                  buttonText={
                    editAdminLevelId
                      ? t('general.common_text.text_18')
                      : t('general.common_text.text_2')
                  }
                />
                <ConditionalRender condition={!!editAdminLevelId}>
                  <Dropdown
                    id="scroll_in_table"
                    placement="auto"
                    className="position-static"
                  >
                    <Dropdown.Toggle className="no-padding no-margin p-0 d-flex bg-transparent">
                      <StandardButton
                        buttonText={capitalizeFirstLetter(
                          t('general.common_text.text_8')
                        )}
                        onClick={() => {}}
                        variant="outline-danger"
                      />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-0">
                      {/* <ConditionalRender
                        condition={checkAccess(
                          [modules.ADMIN_LEVEL.edit],
                          userPermission
                        )}
                      >
                        <Dropdown.Item
                          className="pe-0 border-bottom"
                          onClick={e => {
                            handleSubmit(e, !formData.is_active);
                          }}
                        >
                          <span className="d-flex fs-16 p-0 font-normal">
                            <p className="mb-0">
                              <ConditionalRender
                                condition={formData.is_active}
                                fallBack={t('common_table.text_5')}
                              >
                                {t('common_table.text_4')}
                              </ConditionalRender>
                            </p>
                          </span>
                        </Dropdown.Item>
                      </ConditionalRender> */}
                      <ConditionalRender
                        condition={checkAccess(
                          [modules.ADMIN_LEVEL.delete],
                          userPermission
                        )}
                      >
                        <Dropdown.Item
                          onClick={() => {
                            setDeleteAdminLevel({
                              admin_level_id: parseIntoNumber(editAdminLevelId),
                              showPopup: true
                            });
                          }}
                        >
                          <span className="d-flex fs-16 font-normal p-0">
                            <p className="mb-0">
                              {capitalizeFirstLetter(
                                t('general.common_text.text_14')
                              )}
                            </p>
                          </span>
                        </Dropdown.Item>
                      </ConditionalRender>
                    </Dropdown.Menu>
                  </Dropdown>
                </ConditionalRender>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {/* <ConditionalRender condition={!!editAdminLevelId}>
              <div>
                <StandardTab
                  activeKey={activeTab}
                  data={tabData}
                  onChange={val => setActiveTab(val)}
                />
              </div>
            </ConditionalRender> */}
            <ConditionalRender
              condition={activeTab === LogType.FORM}
              fallBack={
                <InfinityScroll
                  childrenIdPrefix="admin-level-log"
                  currentPage={logCurrentPage}
                  setCurrentPage={setLogCurrentPage}
                  dataLength={logs.length}
                  hasMore={hasMoreLogs}
                  limitPerPage={logPerPage}
                  loading={logLoading}
                  setLoading={setLogLoading}
                >
                  <LogTimeline data={logs} idPrefix="admin-level-log" />
                </InfinityScroll>
              }
            >
              <div>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <StandardInput
                      label={t('admin_level.form.text_1')}
                      placeholder={t('admin_level.form.text_1')}
                      value={formData.level_name}
                      onChange={e =>
                        setFormData({ ...formData, level_name: e as string })
                      }
                      className=""
                      showError={showError}
                      type="text"
                      validations={AdminLevelValidationSet.level_name}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <StandardInput
                      label={t('admin_level.form.text_2')}
                      placeholder={t('admin_level.form.text_2')}
                      value={formData.level_name_german}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          level_name_german: e as string
                        })
                      }
                      className=""
                      showError={showError}
                      type="text"
                      validations={AdminLevelValidationSet.level_name_german}
                    />
                  </div>
                </div>
                <ConditionalRender condition={!editAdminLevelId}>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <StandardInput
                        label={t('admin_level.form.text_3')}
                        placeholder={t('admin_level.form.text_3')}
                        value={formData.level_order}
                        onChange={e =>
                          setFormData({
                            ...formData,
                            level_order: parseIntoNumber(e)
                          })
                        }
                        className=""
                        showError={showError}
                        type="text"
                        validations={AdminLevelValidationSet.level_order}
                      />
                    </div>
                  </div>
                </ConditionalRender>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <StandardInput
                      label={t('admin_level.form.text_4')}
                      placeholder={t('admin_level.form.text_4')}
                      value={formData.description_en || ''}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          description_en: e as string
                        })
                      }
                      className=""
                      showError={showError}
                      type="text"
                      as="textarea"
                      rows={3}
                      validations={
                        AdminLevelValidationSet.description_en as ValidationObj[]
                      }
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <StandardInput
                      label={t('admin_level.form.text_5')}
                      placeholder={t('admin_level.form.text_5')}
                      value={formData.description_de || ''}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          description_de: e as string
                        })
                      }
                      className=""
                      showError={showError}
                      type="text"
                      as="textarea"
                      rows={3}
                      validations={
                        AdminLevelValidationSet.description_de as ValidationObj[]
                      }
                    />
                  </div>
                </div>
              </div>
            </ConditionalRender>
          </Card.Body>
        </Form>
      </ConditionalRender>
      {renderDeletePopup()}
    </Card>
  );
};

export default AdminLevelForm;
